/**
 * Presentation helpers for events, shared by the header switcher (`app/`) and
 * the Event Manager / Event Detail screens (`pages/events/`). Kept in `lib/` so
 * neither side imports the other and the labels exist in exactly one place.
 */
import type { EventStatus, EventType, TeamEvent } from './api-types';
import { EMPTY_VALUE_PLACEHOLDER, parseTimestamp, resolveLocale } from './format';

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  game: 'Game',
  practice: 'Practice',
  scrimmage: 'Scrimmage',
  other: 'Other',
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  pending: 'Scheduled',
  active: 'Active',
  closed: 'Closed',
};

/** FR-EVENTS-07: a closed event is the film-review workflow, not an error. */
export const FILM_REVIEW_LABEL = 'Film Review Mode';
export const CLOSED_EVENT_TOOLTIP =
  'This event is closed. Edits are restricted to Team Admin with recorded reasons.';

/** Badge modifier per status. Closed uses the warning token, never error red. */
export const EVENT_STATUS_BADGE: Record<EventStatus, string> = {
  pending: 'badge',
  active: 'badge badge--brand',
  closed: 'badge badge--warning',
};

const toDate = parseTimestamp;

/**
 * Event dates follow the reader's locale (`lib/format.ts`), not a pinned one:
 * a coach in Germany sees `15.9.2024`, a coach in Texas sees `9/15/2024`.
 */
export function formatEventDate(iso: string | null | undefined, locale?: string): string {
  const date = toDate(iso);
  return date === null ? EMPTY_VALUE_PLACEHOLDER : date.toLocaleDateString(resolveLocale(locale));
}

export function formatEventDateTime(iso: string | null | undefined, locale?: string): string {
  const date = toDate(iso);
  if (date === null) return EMPTY_VALUE_PLACEHOLDER;
  const resolved = resolveLocale(locale);
  return `${date.toLocaleDateString(resolved)}, ${date.toLocaleTimeString(resolved, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

/**
 * Switcher/breadcrumb label. A game against a named opponent reads the way a
 * coach says it out loud — "vs. Lincoln High (9/15/2024)".
 */
export function formatEventLabel(event: TeamEvent): string {
  const opponent = event.opponent_name ?? '';
  const headline = event.event_type === 'game' && opponent !== '' ? `vs. ${opponent}` : event.name;
  return `${headline} (${formatEventDate(event.scheduled_at)})`;
}

/** `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` in local time. */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return '';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  ].join('T');
}

/** Local wall-clock string from the form back to an ISO-8601 instant. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (value.trim() === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Seasons a coach can pick: this year plus the four before it. */
export function seasonYearOptions(nowMs: number = Date.now()): readonly number[] {
  const current = new Date(nowMs).getFullYear();
  return [current + 1, current, current - 1, current - 2, current - 3];
}

export function currentSeasonYear(nowMs: number = Date.now()): number {
  return new Date(nowMs).getFullYear();
}
